import { useEffect, useState } from "react";
import { WifiOff } from "lucide-react";
import { env } from "@/config/envvars";
import { useBackendHealthStore } from "@/lib/backendHealth";
import { PublicShell } from "@/components/gettingIn/PublicShell";
import { Column, GiButton, Note, StatusHead, Ways } from "@/components/gettingIn/parts";
import { clockTime } from "@/components/gettingIn/useResendClock";

/** How often the page asks whether Creo is back (one light request, no query storm). */
const PING_MS = 15_000;

/**
 * Figma 10 · Offline 01, "Creo cannot be reached": shown once the backend has
 * failed to answer MAX_BACKEND_RETRIES times in a row (lib/backendHealth.ts).
 * It replaces the whole route tree, which ends the retry storm. It says it is
 * not the person's fault, that nothing is lost, and never suggests signing
 * out. "We keep trying" is true: a health ping every 15 s brings the app back
 * on its own.
 */
export default function BackendUnavailablePage() {
  const reset = useBackendHealthStore((st) => st.reset);
  const [lastTried, setLastTried] = useState(() => clockTime());
  const [trying, setTrying] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const ping = async () => {
      try {
        const res = await fetch(`${env.BACKEND_URL}/healthz`, { cache: "no-store" });
        if (!cancelled && res.ok) reset();
      } catch {
        /* still down */
      } finally {
        if (!cancelled) setLastTried(clockTime());
      }
    };
    const id = window.setInterval(() => void ping(), PING_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [reset]);

  const tryAgain = async () => {
    setTrying(true);
    try {
      const res = await fetch(`${env.BACKEND_URL}/healthz`, { cache: "no-store" });
      if (res.ok) {
        reset();
        return;
      }
    } catch {
      /* still down */
    }
    setLastTried(clockTime());
    setTrying(false);
  };

  return (
    <PublicShell title="Creo cannot be reached">
      <Column>
        <div role="alert" className="contents">
          <StatusHead icon={WifiOff} title="Creo cannot be reached">
            Your connection may be down, or we are. Nothing is lost; we keep trying.
          </StatusHead>
        </div>
        <Ways>
          <GiButton onClick={() => void tryAgain()} loading={trying}>
            Try again
          </GiButton>
        </Ways>
        <Note>Last tried at {lastTried}.</Note>
      </Column>
    </PublicShell>
  );
}
